import { Colors } from "@/enums/Colors";
import { useResponsive } from "@/hooks/useResponsive";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Fade, IconButton, Modal, SwipeableDrawer, SxProps, Typography, styled, useMediaQuery } from "@mui/material";
import { noop } from "@tanstack/react-query";
import { ReactNode, useId } from "react";

export interface LazoModalProps {
  open: boolean;
  handleClose: () => void;
  children: ReactNode;
  title: string | ReactNode;
  buttons?: ReactNode;
  sx?: SxProps;
  titleSxProps?: SxProps;
  disableSwipeToClose?: boolean;
  /** When true, the close button (X) is disabled and clicking outside won't close the modal */
  disableClose?: boolean;
  /** When true, disables auto focus on first focusable element */
  disableAutoFocus?: boolean;
  /** When true, disables focus enforcement within the modal */
  disableEnforceFocus?: boolean;
  /** When true, disables restoring focus to the previously focused element when modal closes */
  disableRestoreFocus?: boolean;
}

interface ModalContentProps {
  open: boolean;
  sx?: SxProps;
  isMobile?: boolean;
  isTablet?: boolean;
  isDesktop?: boolean;
  children: ReactNode;
}

const defaultTabletStyles = {
  width: "90vw",
  maxWidth: "600px",
  maxHeight: "90vh",
};

const defaultDesktopStyles = {
  width: "auto",
  minWidth: "400px",
  maxWidth: "90vw",
  maxHeight: "90vh",
};

// Desktop fade animation component
function DesktopModalContent({ open, sx, isTablet, isDesktop, children }: ModalContentProps) {
  return (
    <Fade in={open} timeout={{ enter: 300, exit: 300 }}>
      <Container
        sx={{
          ...(isTablet && defaultTabletStyles),
          ...(isDesktop && defaultDesktopStyles),
          ...sx,
        }}
        className="modal-container"
      >
        {children}
      </Container>
    </Fade>
  );
}

export default function LazoModal({
  children,
  handleClose,
  open,
  title,
  buttons,
  sx,
  titleSxProps,
  disableSwipeToClose = false,
  disableClose = false,
  disableAutoFocus = false,
  disableEnforceFocus = false,
  disableRestoreFocus = false,
}: LazoModalProps) {
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const titleId = useId();

  const mobileDrawerContent = (
    <MobileDrawerContainer>
      <DrawerHandle
        onClick={disableClose ? undefined : handleClose}
        role="button"
        aria-label="Close modal"
        tabIndex={disableClose ? -1 : 0}
        onKeyDown={(e) => {
          if (!disableClose && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            handleClose();
          }
        }}
        sx={{ opacity: disableClose ? 0.5 : 1, cursor: disableClose ? "not-allowed" : "pointer" }}
      />
      <DrawerHeader>
        <Typography id={titleId} sx={{ ...titleSxProps, fontSize: "16px", textAlign: "center", width: "100%" }}>
          {title}
        </Typography>
        <CloseButton onClick={handleClose} aria-label="Close modal" size="small" disabled={disableClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DrawerHeader>
      <DrawerContent>{children}</DrawerContent>
      {buttons && <DrawerButtons>{buttons}</DrawerButtons>}
    </MobileDrawerContainer>
  );

  const desktopModalContent = (
    <>
      <DesktopHeader>
        <Typography id={titleId} sx={{ ...titleSxProps, fontSize: "16px" }}>
          {title}
        </Typography>
        <DesktopCloseButton onClick={handleClose} aria-label="Close modal" size="small" disabled={disableClose}>
          <CloseIcon fontSize="small" />
        </DesktopCloseButton>
      </DesktopHeader>

      <DesktopContent>{children}</DesktopContent>

      {buttons && <DesktopButtons>{buttons}</DesktopButtons>}
    </>
  );

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={disableClose ? noop : handleClose}
        onOpen={noop}
        disableSwipeToOpen={true}
        swipeAreaWidth={disableSwipeToClose || disableClose ? 0 : 20}
        hysteresis={0.6}
        minFlingVelocity={400}
        PaperProps={{
          elevation: 24,
          sx: {
            borderTopLeftRadius: { xs: 20, sm: 24 },
            borderTopRightRadius: { xs: 20, sm: 24 },
            maxHeight: { xs: "90vh", sm: "85vh" },
            minHeight: { xs: "30vh", sm: "35vh" },
            background: "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
            width: "100vw !important",
            maxWidth: "100vw !important",
            left: "0 !important",
            right: "0 !important",
            margin: "0 !important",
            boxSizing: "border-box",
            // sx props are ignored on mobile since drawer should always be full width
          },
        }}
        ModalProps={{
          keepMounted: false,
          "aria-labelledby": titleId,
        }}
        transitionDuration={prefersReducedMotion ? 0 : 225}
      >
        {mobileDrawerContent}
      </SwipeableDrawer>
    );
  }

  return (
    <Modal
      open={open}
      onClose={disableClose ? noop : handleClose}
      aria-labelledby={titleId}
      closeAfterTransition
      disableAutoFocus={disableAutoFocus}
      disableEnforceFocus={disableEnforceFocus}
      disableRestoreFocus={disableRestoreFocus}
      slotProps={{ backdrop: { timeout: 300 } }}
    >
      <Container className="root-container" role="dialog" aria-modal="true">
        <DesktopModalContent open={open} sx={sx} isTablet={isTablet} isDesktop={isDesktop}>
          {desktopModalContent}
        </DesktopModalContent>
      </Container>
    </Modal>
  );
}

const MobileDrawerContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  width: "100%",
  maxWidth: "100vw",
  overflow: "hidden",
  boxSizing: "border-box",
});

const DrawerHandle = styled(Box)(({ theme }) => ({
  width: "40px",
  height: "4px",
  backgroundColor: Colors.LIGHT_GRAY_DEDEE1 || "#e0e0e0",
  borderRadius: "2px",
  margin: "8px auto 0",
  cursor: "pointer",
  transition: "all 0.2s ease",
  flexShrink: 0,

  "&:hover": {
    backgroundColor: Colors.DARKER_DARKER_GRAY || "#666",
    width: "48px",
  },

  "&:active": {
    backgroundColor: Colors.LAZO_PURPLE || theme.palette.primary.main,
  },

  "&:focus-visible": {
    outline: `2px solid ${Colors.LAZO_PURPLE || theme.palette.primary.main}`,
    outlineOffset: "2px",
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "12px 20px 16px",
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1 || "#e0e0e0"}`,
  minHeight: "56px",
  backgroundColor: "#fff",
  position: "relative",
  top: 0,
  zIndex: 1,
  flexShrink: 0,
  width: "100%",
  maxWidth: "100vw",
  boxSizing: "border-box",

  [theme.breakpoints.down("sm")]: {
    padding: "10px 16px 14px",
    minHeight: "48px",
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: Colors.DARKER_DARKER_GRAY || "#666",
  padding: "8px",
  transition: "all 0.2s ease",
  position: "absolute",
  right: "20px",
  top: "50%",
  transform: "translateY(-50%)",

  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    color: Colors.ELEGANT_BLACK,
  },

  "&:focus-visible": {
    outline: `2px solid ${Colors.LAZO_PURPLE || theme.palette.primary.main}`,
    outlineOffset: "2px",
  },

  [theme.breakpoints.down("sm")]: {
    right: "16px",
  },
}));

const DrawerContent = styled(Box)({
  overflowY: "auto",
  overflowX: "hidden",
  padding: "1em",
  width: "100%",
  maxWidth: "100vw",
  boxSizing: "border-box",

  // Custom scrollbar for mobile
  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    borderRadius: "2px",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.3)",
    },
  },
});

const DrawerButtons = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "16px 20px 20px",
  borderTop: `1px solid ${Colors.LIGHT_GRAY_DEDEE1 || "#e0e0e0"}`,
  backgroundColor: "#fff",
  flexShrink: 0,
  width: "100%",
  maxWidth: "100vw",
  boxSizing: "border-box",

  "& > *": {
    minHeight: "48px",
  },

  [theme.breakpoints.down("sm")]: {
    padding: "12px 16px 16px",
    gap: "8px",

    "& > *": {
      minHeight: "44px",
    },
  },
}));

// Desktop modal styled components
const Container = styled(Box)(({ theme }) => ({
  "&.root-container": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    padding: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      padding: 0,
      alignItems: "flex-end",
    },
  },
  "&.modal-container": {
    backgroundColor: "white",
    borderRadius: "16px",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 24px 48px -12px rgba(0, 0, 0, 0.18)",
    outline: "none",
    position: "relative",
    [theme.breakpoints.down("md")]: {
      borderRadius: "12px",
    },
  },
}));

const DesktopHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "12px 16px",
  minHeight: "64px",
  backgroundColor: "#fff",
  flexShrink: 0,
  position: "relative",

  [theme.breakpoints.down("md")]: {
    padding: "16px 20px",
    minHeight: "56px",
  },
}));

const DesktopCloseButton = styled(IconButton)(({ theme }) => ({
  color: Colors.DARKER_DARKER_GRAY || "#666",
  padding: "8px",
  transition: "all 0.2s ease",
  position: "absolute",
  right: "16px",
  top: "16px",

  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    color: Colors.ELEGANT_BLACK,
  },

  "&:focus-visible": {
    outline: `2px solid ${Colors.LAZO_PURPLE || theme.palette.primary.main}`,
    outlineOffset: "2px",
  },

  [theme.breakpoints.down("md")]: {
    right: "20px",
    top: "12px",
  },
}));

const DesktopContent = styled(Box)({
  overflowY: "auto",
  overflowX: "hidden",
  padding: "0 1.5em 1em",
  width: "100%",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
});

const DesktopButtons = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: "12px",
  padding: "16px 20px",
  backgroundColor: "#fff",
  flexShrink: 0,
  justifyContent: "flex-end",

  "& > *": {
    flex: "0 1 auto",
    minWidth: "120px",
  },

  // When there are exactly 2 buttons, make them equal width
  "&:has(> :nth-child(2)):not(:has(> :nth-child(3))) > *": {
    flex: "1 1 0",
  },

  [theme.breakpoints.down("md")]: {
    padding: "16px 20px",
    gap: "8px",

    "& > *": {
      width: "100%",
      minWidth: "unset",
    },
  },
}));
